import React from "react";
import { Dialog, DialogContent, DialogActions, Alert, AlertTitle, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CaregiverTeamSelection from "./CaregiverTeamSelection";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useUpdateTeamMembers } from "hooks/api/useUpdateTeamMembers";

interface SimpleDeactivationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  success: boolean;
}

/**
 * Simple deactivation dialog - for users without conflicts
 */
export const SimpleDeactivationDialog: React.FC<SimpleDeactivationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  success,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogContent>
      <Alert severity="error" variant="standard" style={{ marginBottom: "24px" }}>
        <AlertTitle>Are you sure you want to deactivate this user?</AlertTitle>
        Deactivation will suspend account access for the user.
      </Alert>
    </DialogContent>
    <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
      <Button size="medium" color="secondary" variant="contained" onClick={onClose}>
        Cancel
      </Button>
      <LoadingButton
        size="medium"
        color="error"
        variant="contained"
        onClick={onConfirm}
        loading={loading}
        disabled={success}
      >
        Deactivate User
      </LoadingButton>
    </DialogActions>
  </Dialog>
);

interface CaregiverDeactivationDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  isDesignatedCaregiver: boolean;
  onDeactivate?: (reassignments?: Map<number, number>) => void;
  deactivationLoading?: boolean;
}

export const CaregiverDeactivationDialog: React.FC<CaregiverDeactivationDialogProps> = ({
  open,
  onClose,
  userId,
  onDeactivate,
  deactivationLoading,
}) => {
  const { teams } = useGetTeams(
    open && userId ? { userId: userId, includeClientDetails: true } : null
  );

  const teamIds = teams?.map((team: any) => team.id) || [];
  const { teamMembers } = useGetTeamMembers(open && teamIds.length > 0 ? { teamIds } : null);
  const { mutateAsync: updateTeamMembersMutation } = useUpdateTeamMembers() as any;

  const teamsWhereUserIsDesignatedCaregiver = React.useMemo(() => {
    if (!teams || !teamMembers) return [];

    return teams
      .filter((team: any) => {
        const members = teamMembers.filter(
          (member: any) => member.teamId === team.id && member.hasAcceptedInvite
        );
        const designatedCaregiver = members.find((member: any) => member.documentSigner);
        return designatedCaregiver && designatedCaregiver.userId === userId;
      })
      .map((team: any) => team.id);
  }, [teams, teamMembers, userId]);

  const [selectedReassignments, setSelectedReassignments] = React.useState<Map<number, number>>(
    new Map()
  );
  const [isReassigning, setIsReassigning] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setSelectedReassignments(new Map());
      setIsReassigning(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!deactivationLoading && isReassigning) {
      setIsReassigning(false);
    }
  }, [deactivationLoading, isReassigning]);

  const handleSelectionChange = (teamId: number, newCaregiverId: number | null) => {
    setSelectedReassignments((prev) => {
      const newMap = new Map(prev);
      if (newCaregiverId === null) {
        newMap.delete(teamId);
      } else {
        newMap.set(teamId, newCaregiverId);
      }
      return newMap;
    });
  };

  const allTeamsHaveSelection = React.useMemo(() => {
    if (teamsWhereUserIsDesignatedCaregiver.length === 0) {
      return true;
    }

    const selectedTeamIdsAsNumbers = Array.from(selectedReassignments.keys()).map((id: any) =>
      Number(id)
    );
    const requiredTeamIdsAsNumbers = teamsWhereUserIsDesignatedCaregiver.map((id: number) =>
      Number(id)
    );

    return requiredTeamIdsAsNumbers.every((requiredTeamId: number) =>
      selectedTeamIdsAsNumbers.includes(requiredTeamId)
    );
  }, [teamsWhereUserIsDesignatedCaregiver, selectedReassignments]);

  const userIsSelectedAsDesignatedCaregiver = React.useMemo(() => {
    return Array.from(selectedReassignments.values()).some(
      (selectedCaregiverId) => Number(selectedCaregiverId) === Number(userId)
    );
  }, [selectedReassignments, userId]);

  const canDeactivate = allTeamsHaveSelection && !userIsSelectedAsDesignatedCaregiver;

  const handleReassignments = async (reassignments: Map<number, number>): Promise<void> => {
    const reassignmentPromises = Array.from(reassignments.entries()).map(
      async ([teamId, newCaregiverId]) => {
        // Use already-fetched teamMembers data instead of making a new API call
        const teamMembersForTeam = (teamMembers || []).filter(
          (member: any) => member.teamId === teamId
        );

        if (teamMembersForTeam.length === 0) {
          throw new Error(`No team members found for team ${teamId}`);
        }

        // Filter to only accepted members - this is necessary because we need to send
        // ALL accepted members in the update payload, and teamMembers includes both accepted and pending
        const acceptedMembers = teamMembersForTeam.filter(
          (member: any) => member.hasAcceptedInvite
        );

        if (acceptedMembers.length === 0) {
          throw new Error(`No accepted team members found for team ${teamId}`);
        }

        const newCaregiverIdNum = Number(newCaregiverId);
        const updatedTeamMembers = acceptedMembers.map((member: any) => ({
          id: member.id,
          roleId: member.roleId,
          legalGuardian: member.legalGuardian,
          documentSigner: Number(member.userId) === newCaregiverIdNum,
        }));

        const newCaregiverMember = acceptedMembers.find(
          (m: any) => Number(m.userId) === newCaregiverIdNum
        );

        if (!newCaregiverMember) {
          throw new Error(
            `New designated caregiver (userId: ${newCaregiverIdNum}) not found in accepted team members for team ${teamId}`
          );
        }

        return updateTeamMembersMutation({ teamId, teamMembers: updatedTeamMembers });
      }
    );

    await Promise.all(reassignmentPromises);
  };

  const handleDeactivate = async () => {
    if (!onDeactivate || !canDeactivate || isReassigning) {
      return;
    }

    setIsReassigning(true);

    try {
      const reassignmentsToProcess = new Map<number, number>();
      selectedReassignments.forEach((newCaregiverId: number, teamId: number) => {
        reassignmentsToProcess.set(teamId, Number(newCaregiverId));
      });

      if (reassignmentsToProcess.size > 0) {
        await handleReassignments(reassignmentsToProcess);
      }

      if (typeof onDeactivate === "function") {
        onDeactivate(new Map<number, number>());
      } else {
        setIsReassigning(false);
        throw new Error("onDeactivate callback is not a function");
      }
    } catch (error) {
      setIsReassigning(false);
      throw error;
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent dividers>
        <Alert severity="warning" style={{ marginBottom: "24px" }}>
          <AlertTitle>This user is the designated caregiver for a client.</AlertTitle>
          Please choose a new team member to act as the designated caregiver for each team before
          deactivating.
        </Alert>

        <CaregiverTeamSelection
          userId={userId}
          isOpen={open}
          onClose={onClose}
          selectedReassignments={selectedReassignments}
          onSelectionChange={handleSelectionChange}
        />
      </DialogContent>
      <DialogActions style={{ gap: "8px" }}>
        <Button size="medium" color="secondary" variant="contained" onClick={onClose}>
          Close
        </Button>
        <LoadingButton
          size="medium"
          color="error"
          variant="contained"
          onClick={handleDeactivate}
          loading={deactivationLoading || isReassigning}
          disabled={!canDeactivate || isReassigning}
        >
          Deactivate User
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
